import type { CommonData, User } from '../../graphql/codegen';
import type { ReactNode } from 'react';

import { createContext, use, useMemo } from 'react';

import { useCookieContext } from './CookieContext';
import { CustomerType, useCombinedUserAndCommonDataQuery } from '../../graphql/codegen';
import { useAppContext } from '../static/AppContext';

export type UserContextType = {
  user?: User | null;
  commonData?: CommonData | null;
  /**
   * uses _che cookie as a flag indicating if user is logged in or not, should be used in client-side logic only
   *
   * `_che` is present only for logged in
   */
  loggedInCookie: boolean;
  /**
   * used in JSX to check for user type in user data, it is required to prevent hydration issues
   */
  loggedInRender: boolean;
  refetchUser: () => Promise<unknown>;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
  const context = use(UserContext);

  if (context === undefined) {
    throw new Error('`useUserContext()` is used outside of a `<UserProvider />');
  }

  return context;
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { locale, siteType } = useAppContext();
  const { che } = useCookieContext();
  const loggedInCookie = !!che;

  const { error, loading, data, refetch } = useCombinedUserAndCommonDataQuery({
    variables: { locale, siteType },
    ssr: false,
  });

  const value = useMemo(() => {
    if (data && !error && !loading) {
      return {
        user: data.user,
        commonData: data.commonData || null,
        loggedInCookie,
        loggedInRender: data.user?.customerType === CustomerType.Registered,
        refetchUser: refetch,
      };
    }

    return {
      user: null,
      commonData: null,
      loggedInCookie,
      loggedInRender: false,
      refetchUser: refetch,
    };
  }, [data, error, loading, loggedInCookie, refetch]);

  return <UserContext value={value}>{children}</UserContext>;
};
